"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@/lib/api-client";
import type { Reservation } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarX, CalendarCheck, Clock } from "lucide-react";
import {
  formatDate,
  formatPrice,
  nightsBetween,
  statusBadgeClass,
  statusLabel,
  nightLabel,
  bookingTypeLabel,
  bookingTypeBadgeClass,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

export function MyReservationsView() {
  const user = useAppStore((s) => s.user);
  const openAuth = useAppStore((s) => s.openAuth);
  const openProduct = useAppStore((s) => s.openProduct);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState("upcoming");

  const { data, isLoading } = useQuery({
    queryKey: ["reservations", "mine"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>("/api/reservations"),
    enabled: !!user,
  });

  const all = data?.reservations ?? [];

  const cancel = async (id: string) => {
    try {
      await apiPatch(`/api/reservations/${id}`, { action: "cancel" });
      toast.success(t("toast.reservationCancelled"));
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotCancel"));
    }
  };

  if (!user) {
    return (
      <div className="mx-auto grid max-w-7xl place-items-center px-4 py-24 text-center sm:px-6">
        <CalendarCheck className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold">{t("trips.signInTitle")}</h2>
        <p className="mt-1 text-muted-foreground">{t("trips.signInBody")}</p>
        <Button className="mt-4" onClick={() => openAuth("login")}>
          {t("common.signin")}
        </Button>
      </div>
    );
  }

  const now = Date.now();
  const upcoming = all.filter(
    (r) => new Date(r.endDate).getTime() >= now && r.status !== "CANCELLED"
  );
  const past = all.filter(
    (r) => new Date(r.endDate).getTime() < now || r.status === "CANCELLED"
  );

  const list = tab === "upcoming" ? upcoming : past;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight">{t("trips.title")}</h1>
      <p className="mt-1 text-muted-foreground">{t("trips.subtitle")}</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="upcoming">
            {t("trips.upcoming")} ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            {t("trips.history")} ({past.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-4">
          {isLoading ? (
            <div className="grid gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-xl" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed py-16 text-center">
              <CalendarX className="mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium">{t("trips.emptyTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("trips.emptyBody")}</p>
            </div>
          ) : (
            list.map((r) => {
              const nights = nightsBetween(r.startDate, r.endDate);
              return (
                <Card key={r.id} className="overflow-hidden">
                  <div className="flex flex-col gap-4 p-4 sm:flex-row">
                    <div
                      className="aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg bg-muted sm:w-40"
                      onClick={() => r.product && openProduct(r.product.id)}
                    >
                      {r.product?.images?.[0]?.url ? (
                         
                        <img
                          src={r.product.images[0].url}
                          alt={r.product.title}
                          className="h-full w-full cursor-pointer object-cover"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-xs text-muted-foreground">
                          {t("common.noImage")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col justify-between gap-3">
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <button
                              onClick={() => r.product && openProduct(r.product.id)}
                              className="text-left font-semibold hover:underline"
                            >
                              {r.product?.title ?? t("common.loading")}
                            </button>
                            <p className="text-sm text-muted-foreground">
                              {r.product?.location}
                            </p>
                          </div>
                          <Badge className={statusBadgeClass(r.status)}>
                            {statusLabel(r.status, locale)}
                          </Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDate(r.startDate, locale)} → {formatDate(r.endDate, locale)}
                          </span>
                          <span>· {nights} {nightLabel(nights, locale)}</span>
                          <span>· {r.partySize} {t("guests")}</span>
                          <span className="font-medium text-foreground">
                            {formatPrice(r.totalPrice)}
                          </span>
                          {r.bookingType === "SHARED" && (
                            <Badge
                              variant="outline"
                              className={`border ${bookingTypeBadgeClass(r.bookingType)}`}
                            >
                              {bookingTypeLabel(r.bookingType, locale)}
                            </Badge>
                          )}
                        </div>
                        {r.notes && (
                          <p className="mt-2 text-sm text-muted-foreground">“{r.notes}”</p>
                        )}
                        {r.status === "APPROVED" && r.validator && (
                          <p className="mt-1 text-xs text-emerald-700">
                            {t("trips.validatedBy", { name: r.validator.name })}
                          </p>
                        )}
                      </div>
                      {r.status === "PENDING" && (
                        <Button variant="outline" size="sm" className="w-fit" onClick={() => cancel(r.id)}>
                          {t("trips.cancelRequest")}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
